/**
 * Canonical "Integrity Overview" starter manifest + deterministic preflight
 * (Dashboard Builder v1, spec §6 — DASHBOARD_BUILDER_UX_SPEC.md).
 *
 * Five grouped-COUNT widgets — the one widget shape the current wizard can persist
 * (identical to a working severity donut / types bar; see EditSqlViewWidget.vue
 * buildWidgetPayload:707-765). Each resolved seed mirrors that payload exactly:
 * top-level title/subTitle/messageType/fontSize/legend/aiGenerated + top-level grid
 * coords (x/y/width/height) + a JSON-string sqlViewConfig.
 *
 * Source binding (W0.7 portability finding, DASHBOARD_BUILDER_V1_VERIFICATION.md):
 * register views carry PER-FACILITY hash suffixes (e.g. vw_Register_Anomaly_Register_0917e4a0),
 * so register sources bind by PREFIX and resolve 1:1 against the live catalog at
 * preflight; only vw_dashboard_* names are globally stable and bind EXACTly.
 *
 * All-or-none (gate round-2 fix #4): if ANY source is unbindable, a referenced column
 * is missing, a messageType is unsupported, or the geometry is invalid, the ENTIRE
 * Starter is disabled with the reason shown — canonical rows are never cut or substituted.
 */

/** Free-grid column count (CustomDashboard.vue gridColNum:392). Seeds tile rows y0 + y4. */
export const STARTER_GRID_COL_NUM = 18;

/** messageType values the wizard can persist (WidgetDisplayTypePicker.vue:45-70). */
const SUPPORTED_MESSAGE_TYPES = ['datagrid', 'table', 'count', 'bar', 'column', 'line', 'area', 'pie', 'donut'];

const BIND_PREFIX = 'prefix';
const BIND_EXACT = 'exact';

/**
 * The canonical manifest. Column identifiers are the VERBATIM live-catalog names
 * recorded in W0.7 — NOT the spec §6 table's human labels. In particular widget #4's
 * group column is the catalog identifier `CUI Susceptibility`, not the spec label
 * `Susceptibility`; preflight validates each column exists verbatim, so "correcting" a
 * catalog identifier to its label would wrongly disable the Starter on facilities that
 * actually have the data.
 */
export const STARTER_MANIFEST = Object.freeze({
  name: 'Integrity Overview',
  icon: 'mdi-view-dashboard',
  widgets: Object.freeze([
    { title: 'Anomaly Severity Mix', messageType: 'donut', source: { bindType: BIND_PREFIX, value: 'vw_Register_Anomaly_Register' }, column: 'Severity', x: 0, y: 0, width: 9, height: 4 },
    { title: 'Anomaly Types', messageType: 'bar', source: { bindType: BIND_PREFIX, value: 'vw_Register_Anomaly_Register' }, column: 'Anomaly Type', x: 9, y: 0, width: 9, height: 4 },
    { title: 'Anomalies by Component Type', messageType: 'bar', source: { bindType: BIND_PREFIX, value: 'vw_Register_Anomaly_Register' }, column: 'Component Type', x: 0, y: 4, width: 6, height: 4 },
    // Verbatim W0.7 catalog identifier — NOT the spec label 'Susceptibility'.
    { title: 'CUI Susceptibility', messageType: 'donut', source: { bindType: BIND_PREFIX, value: 'vw_Register_CUI_Register' }, column: 'CUI Susceptibility', x: 6, y: 4, width: 6, height: 4 },
    { title: 'Equipment by Class', messageType: 'column', source: { bindType: BIND_EXACT, value: 'vw_dashboard_Equipment' }, column: 'EquipmentClass', x: 12, y: 4, width: 6, height: 4 },
  ]),
});

/**
 * Does a catalog table name satisfy a source binding?
 * - exact: name === value (stable vw_dashboard_* views).
 * - prefix: name === value OR name starts with `value_` — the register hash suffix is
 *   `_<hash>`, so requiring the `_` boundary avoids matching a longer, different view
 *   name (e.g. `vw_Register_Anomaly_RegisterExtra`).
 */
function matchesSource(tableName, bindType, value) {
  if (!tableName) return false;
  if (bindType === BIND_EXACT) return tableName === value;
  return tableName === value || tableName.startsWith(value + '_');
}

/**
 * Resolve each distinct widget source to exactly one live catalog table (1:1).
 * @param {Array<{ name: string, displayName?: string }>} catalogTables — GET api/DashboardWidgets/tables
 * @returns {{ ok: boolean, tableNames?: string[], resolved?: Object, reason?: string }}
 *   `resolved` maps each source `value` → the matched catalog entry; `tableNames` is the
 *   de-duplicated list of resolved names (the three Anomaly widgets share one source).
 */
export function resolveStarterSources(catalogTables) {
  const tables = Array.isArray(catalogTables) ? catalogTables : [];
  const resolved = {};
  for (const widget of STARTER_MANIFEST.widgets) {
    const { bindType, value } = widget.source;
    if (resolved[value]) continue; // dedupe shared sources
    const matches = tables.filter((t) => matchesSource(t && t.name, bindType, value));
    if (matches.length === 0) {
      return { ok: false, reason: `Starter unavailable: source "${value}" was not found on this facility.` };
    }
    if (matches.length > 1) {
      return { ok: false, reason: `Starter unavailable: source "${value}" matched ${matches.length} views on this facility.` };
    }
    resolved[value] = matches[0];
  }
  const tableNames = Object.values(resolved).map((t) => t.name);
  return { ok: true, tableNames, resolved };
}

/** AABB overlap test on two grid rectangles. */
function rectsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x &&
    a.y < b.y + b.height && a.y + a.height > b.y;
}

/** Validate one widget's geometry against the 18-col grid + already-placed widgets. */
function geometryError(widget, placed) {
  const { x, y, width, height, title } = widget;
  if (![x, y, width, height].every(Number.isInteger) || x < 0 || y < 0 || width <= 0 || height <= 0) {
    return `Starter unavailable: widget "${title}" has invalid geometry.`;
  }
  if (x + width > STARTER_GRID_COL_NUM) {
    return `Starter unavailable: widget "${title}" exceeds the ${STARTER_GRID_COL_NUM}-column grid.`;
  }
  for (const other of placed) {
    if (rectsOverlap(widget, other)) {
      return `Starter unavailable: widgets "${title}" and "${other.title}" overlap.`;
    }
  }
  return null;
}

/**
 * Build a fully-resolved widget seed (buildWidgetPayload-shaped) from a manifest widget
 * plus its resolved catalog table + column metadata.
 */
function buildSeed(widget, tableEntry, columnEntry) {
  const sqlViewConfig = {
    tableName: tableEntry.name,
    tableDisplayName: tableEntry.displayName || tableEntry.name,
    columns: [widget.column],
    columnTypes: columnEntry && columnEntry.dataType ? { [widget.column]: columnEntry.dataType } : {},
    columnDisplayNames: columnEntry && columnEntry.displayName ? { [widget.column]: columnEntry.displayName } : {},
    aggregation: {
      groupBy: [widget.column],
      metrics: [{ function: 'COUNT', column: widget.column, alias: '' }],
    },
    filters: [],
    link3D: false,
    tagColumn: null,
    coloring: null,
    refreshIntervalMinutes: null,
    exportEnabled: true,
    columnWidthMode: 'autoFit',
  };
  return {
    title: widget.title,
    subTitle: '',
    messageType: widget.messageType,
    fontSize: 1,
    legend: { show: true, position: 'bottom' },
    aiGenerated: false,
    x: widget.x,
    y: widget.y,
    width: widget.width,
    height: widget.height,
    sqlViewConfig: JSON.stringify(sqlViewConfig),
  };
}

/**
 * Deterministic preflight for the starter preset (spec §6). All-or-none — the first
 * failure disables the entire Starter with a human reason; success returns the resolved
 * manifest (complete seed payloads ready for the transactional create endpoint).
 *
 * @param {Array<{ name: string, displayName?: string }>} catalogTables — GET .../tables
 * @param {Object<string, Array<{ name: string, dataType?: string, displayName?: string }>>} columnsByTable
 *   keyed by RESOLVED table name (caller fetches columns for resolveStarterSources().tableNames).
 * @returns {{ ok: boolean, resolvedManifest?: { name, icon, widgets: object[] }, reason?: string }}
 */
export function preflightStarter(catalogTables, columnsByTable) {
  const resolution = resolveStarterSources(catalogTables);
  if (!resolution.ok) return { ok: false, reason: resolution.reason };

  const cols = columnsByTable || {};
  const placed = [];
  const seeds = [];

  for (const widget of STARTER_MANIFEST.widgets) {
    const tableEntry = resolution.resolved[widget.source.value];
    const tableColumns = Array.isArray(cols[tableEntry.name]) ? cols[tableEntry.name] : [];
    const columnEntry = tableColumns.find((c) => c && c.name === widget.column);
    if (!columnEntry) {
      return { ok: false, reason: `Starter unavailable: column "${widget.column}" was not found in ${tableEntry.name}.` };
    }
    if (!SUPPORTED_MESSAGE_TYPES.includes(widget.messageType)) {
      return { ok: false, reason: `Starter unavailable: chart type "${widget.messageType}" is not supported.` };
    }
    const geomError = geometryError(widget, placed);
    if (geomError) return { ok: false, reason: geomError };
    placed.push(widget);
    seeds.push(buildSeed(widget, tableEntry, columnEntry));
  }

  return {
    ok: true,
    resolvedManifest: { name: STARTER_MANIFEST.name, icon: STARTER_MANIFEST.icon, widgets: seeds },
  };
}
